// src/components/issues/ListeProblemesTelephone.tsx — SCE Issues
// Les problèmes d'une liste au téléphone (« Tous les problèmes », « Mes
// déclarations ») : rangées denses de 64 px dans une carte, comme l'accueil et
// la fenêtre de liste (30/09). Remplace les cartes IssueCard de la V1 (112 px
// chacune, deux bulles par ligne).
//
// Le titre d'abord ; en dessous N° · machine · ancienneté ; à droite l'état
// (points de criticité + cercle de statut, ui/EtatProbleme). Le bureau garde IssueRow.
//
// `fusionnerSoeurs` (26/10) : les fiches d'un même défaut multi-équipements sur
// une rangée, une étiquette par équipement (comme le tableau du bureau).

import { LigneProbleme } from '@/components/accueil/LigneProbleme'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { METIERS_COULEURS, METIERS_LABELS_COURT } from '@/config/metiers.config'
import { useMaintenant } from '@/hooks/useMaintenant'
import type { Issue } from '@/types/issue.types'
import { libelleAge } from '@/utils/accueil'
import { lienProbleme } from '@/utils/liensFiches'
import { fusionnerSoeurs as fusionner, moinsAvancee } from '@/utils/tableauProblemes'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { EtiquettesSoeurs } from './EtiquettesSoeurs'

export function ListeProblemesTelephone({
  issues,
  fusionnerSoeurs = false,
}: {
  issues: readonly Issue[]
  fusionnerSoeurs?: boolean
}): JSX.Element {
  const maintenant = useMaintenant()
  const rangees = fusionnerSoeurs ? fusionner(issues) : issues.map((issue) => ({ issue, soeurs: undefined }))
  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {rangees.map(({ issue, soeurs }) =>
        soeurs ? (
          <LigneSoeurs key={issue.id} issue={issue} soeurs={soeurs} meta={[issue.id, libelleAge(issue.dateDeclaration, maintenant)].join(' · ')} />
        ) : (
          <LigneProbleme
            key={issue.id}
            issue={issue}
            deuxLignes
            meta={[issue.id, issue.numeroSerie, libelleAge(issue.dateDeclaration, maintenant)].filter(Boolean).join(' · ')}
            droite={<EtatProbleme statut={issue.statut} criticite={issue.criticite} />}
          />
        ),
      )}
    </div>
  )
}

/**
 * La rangée d'un défaut sur plusieurs équipements. Le bouton principal couvre la
 * rangée (::after, comme l'activité récente) ; les étiquettes passent au-dessus
 * et ouvrent chacune SA fiche. État = la fiche la moins avancée.
 */
function LigneSoeurs({ issue, soeurs, meta }: { issue: Issue; soeurs: readonly Issue[]; meta: string }): JSX.Element {
  const ouvrirFiche = useOuvrirFiche()
  const etat = moinsAvancee(soeurs)
  return (
    <div className="liste-soeurs-tel">
      <span style={{ alignSelf: 'stretch', backgroundColor: METIERS_COULEURS[issue.metier] }} title={METIERS_LABELS_COURT[issue.metier]}>
        <span className="sr-only">{METIERS_LABELS_COURT[issue.metier]}</span>
      </span>
      <span className="flex flex-col min-w-0" style={{ gap: 6, padding: '9px 0 11px' }}>
        <button
          type="button"
          className="ligne-activite-principal liste-soeurs-tel-principal"
          onClick={() => ouvrirFiche(lienProbleme(issue.id))}
        >
          <span className="liste-soeurs-tel-titre">{issue.titre}</span>
          <span className="liste-soeurs-tel-meta">{meta}</span>
        </button>
        <EtiquettesSoeurs soeurs={soeurs} onOuvrir={(s) => ouvrirFiche(lienProbleme(s.id))} />
      </span>
      <EtatProbleme statut={etat.statut} criticite={etat.criticite} />
    </div>
  )
}
